import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { api } from '../services/api'
import {
  clearFoodSessionStorage,
  getFoodStorageItem,
  setFoodStorageItem,
} from '../utils/embeddedStorage'

interface AuthState {
  user: any | null
  accessToken: string | null
  subscription: any | null
  loading: boolean
  error: string | null
  meReady: boolean
}

const initialState: AuthState = {
  user: JSON.parse(getFoodStorageItem('user') || 'null'),
  accessToken: getFoodStorageItem('token'),
  subscription: JSON.parse(getFoodStorageItem('subscription') || 'null'),
  loading: false,
  error: null,
  meReady: !getFoodStorageItem('token'),
}

function followEntryRedirect(err: any): string | null {
  const payload = err?.response?.data || {}
  const redirectUrl = payload.redirect_url || payload.redirectUrl
  if ((payload.code === 'ENTRY_REDIRECT' || err?.response?.status === 409) && redirectUrl) {
    clearFoodSessionStorage()
    window.location.assign(String(redirectUrl))
    return 'ENTRY_REDIRECT'
  }
  return null
}

export const login = createAsyncThunk('auth/login', async (dto: { email: string; password: string }, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/auth/login', dto)
    return data
  } catch (err: any) {
    const redirected = followEntryRedirect(err)
    if (redirected) return rejectWithValue(redirected)
    const payload = err.response?.data || {}
    return rejectWithValue(payload.message || payload.error || 'Error al iniciar sesión')
  }
})

export const register = createAsyncThunk('auth/register', async (dto: any, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/auth/register', dto)
    return data
  } catch (err: any) {
    const redirected = followEntryRedirect(err)
    if (redirected) return rejectWithValue(redirected)
    const payload = err.response?.data || {}
    return rejectWithValue(payload.message || payload.error || 'Error al registrarse')
  }
})

export const getMe = createAsyncThunk('auth/me', async (_, { rejectWithValue }) => {
  try {
    const { data } = await api.get('/auth/me')
    return data
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message)
  }
})

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.user = null
      state.accessToken = null
      state.subscription = null
      state.meReady = true
      clearFoodSessionStorage()
    },
    setToken(state, action: PayloadAction<string>) {
      state.accessToken = action.payload
      setFoodStorageItem('token', action.payload)
    },
    setSession(state, action: PayloadAction<any>) {
      state.loading = false
      state.error = null
      const payload = action.payload || {}
      state.user = {
        ...(payload.user || {}),
        planExpired: payload.planExpired === true || payload.user?.planExpired === true,
        pendingActivation: payload.pendingActivation === true || payload.user?.pendingActivation === true,
        renewUrl: payload.renewUrl || payload.renew_url || payload.user?.renewUrl || '',
      }
      state.accessToken = payload.accessToken
      state.subscription = payload.subscription
      state.meReady = true
      setFoodStorageItem('token', payload.accessToken)
      setFoodStorageItem('refresh', payload.refreshToken || null)
      if (state.user) {
        setFoodStorageItem('user', JSON.stringify(state.user))
      }
      setFoodStorageItem('subscription', JSON.stringify(payload.subscription ?? null))
    },
  },
  extraReducers: (builder) => {
    const handleAuth = (state: AuthState, action: PayloadAction<any>) => {
      state.loading = false
      state.error = null
      const payload = action.payload || {}
      state.user = {
        ...(payload.user || {}),
        planExpired: payload.planExpired === true || payload.user?.planExpired === true,
        renewUrl: payload.renewUrl || payload.renew_url || payload.user?.renewUrl || '',
        pendingActivation: payload.pendingActivation === true || payload.user?.pendingActivation === true,
      }
      state.accessToken = payload.accessToken
      state.subscription = payload.subscription
      state.meReady = true
      setFoodStorageItem('token', payload.accessToken)
      setFoodStorageItem('refresh', payload.refreshToken || null)
      setFoodStorageItem('user', JSON.stringify(state.user))
      setFoodStorageItem('subscription', JSON.stringify(payload.subscription))
    }
    builder
      .addCase(login.pending, (s) => { s.loading = true; s.error = null })
      .addCase(login.fulfilled, handleAuth)
      .addCase(login.rejected, (s, a) => { s.loading = false; s.error = a.payload as string })
      .addCase(register.pending, (s) => { s.loading = true; s.error = null })
      .addCase(register.fulfilled, handleAuth)
      .addCase(register.rejected, (s, a) => { s.loading = false; s.error = a.payload as string })
      .addCase(getMe.fulfilled, (s, a) => {
        s.meReady = true
        if (a.payload) {
          s.user = {
            ...a.payload,
            pendingActivation: a.payload.pendingActivation === true || a.payload.user?.pendingActivation === true,
            planExpired: a.payload.planExpired === true || a.payload.user?.planExpired === true,
            renewUrl: a.payload.renewUrl || a.payload.renew_url || a.payload.user?.renewUrl || '',
          }
          setFoodStorageItem('user', JSON.stringify(s.user))
          if (a.payload.subscription !== undefined) {
            s.subscription = a.payload.subscription
            setFoodStorageItem('subscription', JSON.stringify(a.payload.subscription))
          }
        }
      })
      .addCase(getMe.rejected, (s) => {
        s.meReady = true
      })
  },
})

export const { logout, setToken, setSession } = authSlice.actions
export default authSlice.reducer
